import React from "react";
import { Box, Modal, ModalDialog, IconButton, Textarea } from "@mui/joy";
import DarkTypography from "./DarkTypography";
import DarkCheckbox from "./DarkCheckbox";
import type { MultiComment } from "../../api/zekay/comments";
import { formatMultiCommentDateForDisplay } from "../../api/zekay/comments";
import { SDelete, CloseRoundedIcon } from "../../styles/StyledMUI";
import { colorTokens } from "../../styles/globalStyles";

interface CommentsModalProps {
  open: boolean;
  title?: string;
  currentUserName?: string;
  multiComments?: MultiComment[] | undefined;
  setMultiComments?: React.Dispatch<React.SetStateAction<MultiComment[] | undefined>>;
  onSave: (updated?: MultiComment[]) => void;
  onClose: () => void;
  allowEdit?: boolean;
  showPdfSheetOption?: boolean;
}

const PDF_IMPORTANT_HINT = "Un seul commentaire peut être affiché sur la feuille de sortie (PDF)";

function stripImportantFromAll(list: MultiComment[]): MultiComment[] {
  return list.map((c) => {
    if (c.Important !== true) return c;
    const copy: MultiComment = { ...c };
    delete copy.Important;

    return copy;
  });
}

/** At most one comment has Important; index `keep` is set true, others cleared. */
function setOnlyImportantAt(list: MultiComment[], keep: number): MultiComment[] {
  return list.map((c, j) => {
    const copy: MultiComment = { ...c };
    if (j === keep) {
      copy.Important = true;
    } else {
      delete copy.Important;
    }

    return copy;
  });
}

export default function CommentsModal(props: CommentsModalProps) {
  const [newDraft, setNewDraft] = React.useState("");
  const [newImportant, setNewImportant] = React.useState(false);

  React.useEffect(() => {
    if (!props.open) return;

    setNewDraft("");
    setNewImportant(false);
  }, [props.open]);

  const persist = (next: MultiComment[]) => {
    props.setMultiComments?.(next);
    props.onSave(next);
  };

  const addComment = async () => {
    const trimmed = newDraft.trim();
    if (!trimmed) return;
    const entry: MultiComment = {
      User: props.currentUserName || "User",
      Comment: trimmed,
      Date: new Date().toISOString(),
    };
    const base = props.multiComments || [];
    const withoutOldImportant =
      props.showPdfSheetOption === true && newImportant ? stripImportantFromAll(base) : base;
    if (props.showPdfSheetOption === true && newImportant) {
      entry.Important = true;
    }
    const next: MultiComment[] = [...withoutOldImportant, entry];
    persist(next);
    setNewDraft("");
    setNewImportant(false);
  };

  const deleteAt = (idx: number) => {
    const base = props.multiComments || [];
    const next = [...base.slice(0, idx), ...base.slice(idx + 1)];
    persist(next);
  };

  const setImportantAt = (idx: number, important: boolean) => {
    const base = props.multiComments || [];
    if (!important) {
      const next = base.map((c, j) => {
        if (j !== idx) return c;
        const copy: MultiComment = { ...c };
        delete copy.Important;

        return copy;
      });
      persist(next);

      return;
    }
    persist(setOnlyImportantAt(base, idx));
  };

  return (
    <Modal
      open={props.open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        props.onClose();
      }}
    >
      <ModalDialog
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        sx={{
          bgcolor: colorTokens.bg.surface,
          border: `1px solid ${colorTokens.border.default}`,
          maxWidth: { xs: "95vw", sm: 720 },
          width: { xs: "95vw", sm: "90%" },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <DarkTypography level="h4" colorVariant="title">
            {props.title || "Commentaires"}
          </DarkTypography>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              props.onClose();
            }}
            sx={{
              color: colorTokens.accent.primary,
              "&:hover": { backgroundColor: colorTokens.bg.surfaceElevated },
            }}
            title="Fermer"
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        {/* List */}
        <Box
          sx={{
            maxHeight: 320,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {(props.multiComments?.length || 0) === 0 ? (
            <DarkTypography colorVariant="muted">Aucun commentaire.</DarkTypography>
          ) : (
            (props.multiComments || []).map((c, i) => (
              <Box
                key={`${c.User}-${c.Date}-${i}`}
                sx={{
                  p: 1,
                  border: `1px solid ${colorTokens.border.default}`,
                  borderRadius: 1.5,
                  backgroundColor: colorTokens.bg.surfaceElevated,
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <DarkTypography
                    colorVariant="primary"
                    sx={{ fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
                  >
                    {c.User}
                  </DarkTypography>
                  <DarkTypography
                    colorVariant="primary"
                    sx={{ whiteSpace: "pre-wrap", fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                  >
                    {c.Comment}
                  </DarkTypography>
                  <DarkTypography
                    sx={{
                      color: colorTokens.text.muted,
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    }}
                  >
                    {formatMultiCommentDateForDisplay(c.Date)}
                  </DarkTypography>
                </Box>
                {props.allowEdit !== false &&
                  props.currentUserName &&
                  c.User === props.currentUserName && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 0.25,
                        flexShrink: 0,
                        pt: 0.25,
                      }}
                    >
                      {props.showPdfSheetOption === true && (
                        <>
                          <DarkCheckbox
                            checked={c.Important === true}
                            onChange={(ev) => setImportantAt(i, ev.target.checked)}
                            aria-label={PDF_IMPORTANT_HINT}
                            title={PDF_IMPORTANT_HINT}
                            sx={{ p: 0, minHeight: 0, "--Checkbox-size": "16px" }}
                          />
                          <Box
                            component="span"
                            aria-hidden
                            title={PDF_IMPORTANT_HINT}
                            sx={{ fontSize: "1rem", lineHeight: 1, userSelect: "none" }}
                          >
                            ⚠️
                          </Box>
                        </>
                      )}
                      <IconButton
                        size="sm"
                        onClick={() => deleteAt(i)}
                        sx={{ color: colorTokens.status.danger }}
                        title="Supprimer"
                      >
                        <SDelete />
                      </IconButton>
                    </Box>
                  )}
              </Box>
            ))
          )}
        </Box>

        {/* Add */}
        {props.allowEdit !== false && (
          <Box sx={{ mt: 1 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1,
                alignItems: { sm: "flex-start" },
              }}
            >
              <Textarea
                minRows={3}
                value={newDraft}
                onChange={(e) => setNewDraft(e.target.value)}
                sx={{
                  backgroundColor: colorTokens.bg.surfaceElevated,
                  color: colorTokens.text.primary,
                  borderColor: colorTokens.border.default,
                  flexGrow: 1,
                }}
                placeholder="Ajouter un commentaire"
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 0.25,
                  alignSelf: { xs: "flex-end", sm: "center" },
                  flexShrink: 0,
                }}
              >
                {props.showPdfSheetOption === true && (
                  <>
                    <DarkCheckbox
                      checked={newImportant}
                      onChange={(ev) => setNewImportant(ev.target.checked)}
                      aria-label={PDF_IMPORTANT_HINT}
                      title={PDF_IMPORTANT_HINT}
                      sx={{ p: 0, minHeight: 0, "--Checkbox-size": "16px" }}
                    />
                    <Box
                      component="span"
                      aria-hidden
                      title={PDF_IMPORTANT_HINT}
                      sx={{ fontSize: "1rem", lineHeight: 1, userSelect: "none" }}
                    >
                      ⚠️
                    </Box>
                  </>
                )}
                <IconButton
                  onClick={addComment}
                  disabled={!newDraft.trim()}
                  sx={{
                    color: newDraft.trim() ? colorTokens.accent.primary : colorTokens.text.muted,
                  }}
                  title="Ajouter"
                >
                  +
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}
      </ModalDialog>
    </Modal>
  );
}
